import { supabase } from '../lib/supabase';
import { isDemoMode, getDemoOrgId, getAuthOrgId } from '../utils/demoHelper';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

// Check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Build a date filter cutoff from a dateRange string ('7','30','90','365','all')
function getDateCutoff(dateRange) {
  if (!dateRange || dateRange === 'all') return null;
  const days = parseInt(dateRange, 10);
  if (isNaN(days)) return null;
  const cutoff = new Date();
  cutoff?.setDate(cutoff?.getDate() - days);
  return cutoff?.toISOString();
}

export const analyticsService = {
  // Get projects grouped by phase, filtered by dateRange and projectType
  async getProjectsByPhase(dateRange = 'all', projectType = 'all') {
    try {
      let query = supabase
        ?.from('projects')
        ?.select('phase, adu_type, created_at')
        ?.order('phase');

      // Demo mode: filter by demo org_id
      if (isDemoMode()) {
        const demoOrgId = getDemoOrgId();
        if (demoOrgId) query = query?.eq('organization_id', demoOrgId);
      } else {
        // Use cached org_id from AuthContext (never stale after token refresh)
        const orgId = getAuthOrgId();
        if (orgId) query = query?.eq('organization_id', orgId);
      }

      const cutoff = getDateCutoff(dateRange);
      if (cutoff) {
        query = query?.gte('created_at', cutoff);
      }
      if (projectType && projectType !== 'all') {
        query = query?.eq('adu_type', projectType);
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      // Count projects by phase
      const phaseCounts = {};
      data?.forEach(project => {
        const phase = project?.phase || 'unknown';
        phaseCounts[phase] = (phaseCounts?.[phase] || 0) + 1;
      });

      return Object.entries(phaseCounts)?.map(([phase, count]) => ({
        phase,
        count
      }));
    } catch (error) {
      console.error('Error fetching projects by phase:', error);
      return [];
    }
  },

  // Get revenue pipeline data, filtered by dateRange and projectType
  async getRevenuePipeline(dateRange = 'all', projectType = 'all') {
    try {
      let query = supabase
        ?.from('projects')
        ?.select('phase, adu_type, budget_total, budget_spent, created_at')
        ?.order('phase');

      // Demo mode: filter by demo org_id
      if (isDemoMode()) {
        const demoOrgId = getDemoOrgId();
        if (demoOrgId) query = query?.eq('organization_id', demoOrgId);
      } else {
        // Use cached org_id from AuthContext (never stale after token refresh)
        const orgId = getAuthOrgId();
        if (orgId) query = query?.eq('organization_id', orgId);
      }

      const cutoff = getDateCutoff(dateRange);
      if (cutoff) {
        query = query?.gte('created_at', cutoff);
      }
      if (projectType && projectType !== 'all') {
        query = query?.eq('adu_type', projectType);
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      // Aggregate revenue by phase
      const revenueByPhase = {};
      data?.forEach(project => {
        const phase = project?.phase || 'unknown';
        if (!revenueByPhase?.[phase]) {
          revenueByPhase[phase] = {
            projected: 0,
            actual: 0
          };
        }
        revenueByPhase[phase].projected += parseFloat(project?.budget_total || 0);
        revenueByPhase[phase].actual += parseFloat(project?.budget_spent || 0);
      });

      return Object.entries(revenueByPhase)?.map(([phase, revenue]) => ({
        phase,
        projected: Math.round(revenue?.projected),
        actual: Math.round(revenue?.actual)
      }));
    } catch (error) {
      console.error('Error fetching revenue pipeline:', error);
      return [];
    }
  },

  // Get permit performance metrics, filtered by dateRange
  async getPermitPerformance(dateRange = 'all') {
    try {
      let query = supabase
        ?.from('permits')
        ?.select('status, submitted_date, deadline')
        ?.order('submitted_date', { ascending: false });

      // Demo mode: filter by demo org_id
      if (isDemoMode()) {
        const demoOrgId = getDemoOrgId();
        if (demoOrgId) query = query?.eq('organization_id', demoOrgId);
      } else {
        // Use cached org_id from AuthContext (never stale after token refresh)
        const orgId = getAuthOrgId();
        if (orgId) query = query?.eq('organization_id', orgId);
      }

      const cutoff = getDateCutoff(dateRange);
      if (cutoff) {
        query = query?.gte('submitted_date', cutoff);
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return { approvalRate: 0, avgProcessingTime: 0, statusBreakdown: [] };
        } else {
          console.log('Data error:', error?.message);
          return { approvalRate: 0, avgProcessingTime: 0, statusBreakdown: [] };
        }
      }

      const statusCounts = {};
      let totalProcessingTime = 0;
      let processedCount = 0;
      let approvedCount = 0;

      data?.forEach(permit => {
        const status = permit?.status || 'unknown';
        statusCounts[status] = (statusCounts?.[status] || 0) + 1;

        if (status === 'approved' || status === 'rejected') {
          approvedCount += status === 'approved' ? 1 : 0;
          processedCount++;

          // Calculate processing time from submission to deadline
          if (permit?.submitted_date && permit?.deadline) {
            const submitted = new Date(permit.submitted_date);
            const deadline = new Date(permit.deadline);
            const days = Math.abs((deadline - submitted) / (1000 * 60 * 60 * 24));
            totalProcessingTime += days;
          }
        }
      });

      const approvalRate = processedCount > 0 ? (approvedCount / processedCount) * 100 : 0;
      const avgProcessingTime = processedCount > 0 ? totalProcessingTime / processedCount : 0;

      const statusBreakdown = Object.entries(statusCounts)?.map(([status, count]) => ({
        status,
        count
      }));

      return {
        approvalRate: Math.round(approvalRate * 10) / 10,
        avgProcessingTime: Math.round(avgProcessingTime),
        statusBreakdown
      };
    } catch (error) {
      console.error('Error fetching permit performance:', error);
      return { approvalRate: 0, avgProcessingTime: 0, statusBreakdown: [] };
    }
  },

  // Get contractor performance metrics
  async getContractorPerformance() {
    try {
      let query = supabase
        ?.from('contractors')
        ?.select('name, rating, quality_score, timeline_adherence, customer_satisfaction, completion_rate, total_projects, completed_projects')
        ?.order('rating', { ascending: false })
        ?.limit(10);

      // Demo mode: filter by demo org_id
      if (isDemoMode()) {
        const demoOrgId = getDemoOrgId();
        if (demoOrgId) query = query?.eq('organization_id', demoOrgId);
      } else {
        // Use cached org_id from AuthContext (never stale after token refresh)
        const orgId = getAuthOrgId();
        if (orgId) query = query?.eq('organization_id', orgId);
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching contractor performance:', error);
      return [];
    }
  },

  // Get team productivity metrics, filtered by dateRange
  async getTeamProductivity(dateRange = 'all') {
    try {
      let query = supabase
        ?.from('activity_logs')
        ?.select('user_id, action, created_at, user_profiles(full_name, department)')
        ?.order('created_at', { ascending: false })
        ?.limit(200);

      // Demo mode: filter by user_ids belonging to the demo org
      if (isDemoMode()) {
        const demoOrgId = getDemoOrgId();
        if (demoOrgId) {
          const { data: orgUsers } = await supabase
            ?.from('user_profiles')
            ?.select('id')
            ?.eq('organization_id', demoOrgId);
          if (orgUsers && orgUsers?.length > 0) {
            const userIds = orgUsers?.map(u => u?.id);
            query = query?.in('user_id', userIds);
          }
        }
      } else {
        // Use cached org_id from AuthContext (never stale after token refresh)
        const orgId = getAuthOrgId();
        if (orgId) {
          const { data: orgUsers } = await supabase
            ?.from('user_profiles')
            ?.select('id')
            ?.eq('organization_id', orgId);
          if (orgUsers && orgUsers?.length > 0) {
            const userIds = orgUsers?.map(u => u?.id);
            query = query?.in('user_id', userIds);
          }
        }
      }

      const cutoff = getDateCutoff(dateRange);
      if (cutoff) {
        query = query?.gte('created_at', cutoff);
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return { activityByUser: [], activityByAction: [], recentActivity: [] };
        } else {
          console.log('Data error:', error?.message);
          return { activityByUser: [], activityByAction: [], recentActivity: [] };
        }
      }

      const activityByUser = {};
      const activityByAction = {};

      data?.forEach(activity => {
        const userName = activity?.user_profiles?.full_name || 'Unknown';
        const action = activity?.action || 'unknown';

        activityByUser[userName] = (activityByUser?.[userName] || 0) + 1;
        activityByAction[action] = (activityByAction?.[action] || 0) + 1;
      });

      return {
        activityByUser: Object.entries(activityByUser)?.map(([user, count]) => ({ user, count })),
        activityByAction: Object.entries(activityByAction)?.map(([action, count]) => ({ action, count })),
        recentActivity: toCamelCase(data?.slice(0, 10) || [])
      };
    } catch (error) {
      console.error('Error fetching team productivity:', error);
      return { activityByUser: [], activityByAction: [], recentActivity: [] };
    }
  }
};